import { useEffect, useState } from "react";
import { Bell, BellOff, Check, CheckCheck, Loader2, Plus, Trash2 } from "lucide-react";
import { api } from "@/api/client";
import type { AlertCondition, AlertEvent, AlertMetric, AlertRule, Brand } from "../../../types";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { EmptyState } from "@/components/ui/empty-state";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { NativeSelect } from "@/components/ui/native-select";
import { cn } from "@/lib/utils";

const METRIC_OPTIONS: { value: AlertMetric; label: string }[] = [
  { value: "visibility", label: "Visibility" },
  { value: "sentiment", label: "Sentiment" },
  { value: "overall_score", label: "Score" },
];

const CONDITION_OPTIONS: { value: AlertCondition; label: string }[] = [
  { value: "below", label: "falls below" },
  { value: "above", label: "rises above" },
  { value: "drops_by", label: "drops by (vs 7-day avg)" },
];

const metricLabel = (m: string) => METRIC_OPTIONS.find(o => o.value === m)?.label ?? m;
const conditionLabel = (c: string) => CONDITION_OPTIONS.find(o => o.value === c)?.label ?? c;

function describeRule(rule: AlertRule): string {
  return `${rule.brand_name} ${metricLabel(rule.metric).toLowerCase()} ${conditionLabel(rule.condition)} ${rule.threshold}`;
}

export function AlertsView() {
  const [rules, setRules] = useState<AlertRule[]>([]);
  const [events, setEvents] = useState<AlertEvent[]>([]);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  // Create-form state
  const [brandId, setBrandId] = useState("");
  const [metric, setMetric] = useState<AlertMetric>("visibility");
  const [condition, setCondition] = useState<AlertCondition>("below");
  const [threshold, setThreshold] = useState("50");
  const [webhookUrl, setWebhookUrl] = useState("");
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");

  const fetchAll = async () => {
    try {
      const [r, e] = await Promise.all([api.alerts.rules(), api.alerts.events()]);
      setRules(r);
      setEvents(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    api.brands.list().then(list => {
      setBrands(list);
      if (list.length > 0) setBrandId(String(list[0]!.id));
    }).catch(() => {});
  }, []);

  const handleCreate = async () => {
    const parsed = parseFloat(threshold);
    if (!brandId || isNaN(parsed)) { setFormError("Pick a brand and a numeric threshold"); return; }
    setSaving(true);
    setFormError("");
    try {
      await api.alerts.createRule({
        brand_id: Number(brandId),
        metric,
        condition,
        threshold: parsed,
        webhook_url: webhookUrl.trim() || undefined,
      });
      setShowForm(false);
      setWebhookUrl("");
      fetchAll();
    } catch (err: any) {
      setFormError(err?.message || "Failed to create rule");
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (rule: AlertRule) => {
    await api.alerts.updateRule(rule.id, { enabled: !rule.enabled });
    fetchAll();
  };

  const handleDeleteRule = async (id: number) => {
    if (!confirm("Delete this alert rule? Its past alerts will be removed too.")) return;
    await api.alerts.deleteRule(id);
    fetchAll();
  };

  const handleAck = async (id: number) => {
    await api.alerts.ack(id);
    setEvents(prev => prev.map(e => (e.id === id ? { ...e, acknowledged: true } : e)));
  };

  const handleAckAll = async () => {
    await api.alerts.ackAll();
    setEvents(prev => prev.map(e => ({ ...e, acknowledged: true })));
  };

  const unacked = events.filter(e => !e.acknowledged).length;

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2].map(i => <div key={i} className="h-36 bg-slate-100 rounded-lg animate-subtle-pulse" />)}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Rules */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Alert Rules</CardTitle>
              <CardDescription>Watch a brand's metric and get notified when it crosses a threshold</CardDescription>
            </div>
            <Button size="sm" onClick={() => setShowForm(!showForm)}>
              <Plus className="w-4 h-4 mr-1.5" />
              New Rule
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {showForm && (
            <div className="mb-5 p-4 rounded-lg border border-slate-200 bg-slate-50 animate-fade-up space-y-4">
              <div className="grid gap-4 sm:grid-cols-4">
                <div>
                  <Label htmlFor="rule-brand">Brand</Label>
                  <NativeSelect id="rule-brand" value={brandId} onChange={e => setBrandId(e.target.value)}>
                    {brands.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                  </NativeSelect>
                </div>
                <div>
                  <Label htmlFor="rule-metric">Metric</Label>
                  <NativeSelect id="rule-metric" value={metric} onChange={e => setMetric(e.target.value as AlertMetric)}>
                    {METRIC_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </NativeSelect>
                </div>
                <div>
                  <Label htmlFor="rule-condition">Condition</Label>
                  <NativeSelect id="rule-condition" value={condition} onChange={e => setCondition(e.target.value as AlertCondition)}>
                    {CONDITION_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </NativeSelect>
                </div>
                <div>
                  <Label htmlFor="rule-threshold">{condition === "drops_by" ? "Points" : "Threshold"}</Label>
                  <Input id="rule-threshold" type="number" value={threshold} onChange={e => setThreshold(e.target.value)} />
                </div>
              </div>
              <div>
                <Label htmlFor="rule-webhook">Webhook URL (optional, Slack-compatible)</Label>
                <Input
                  id="rule-webhook"
                  placeholder="https://hooks.slack.com/services/…"
                  value={webhookUrl}
                  onChange={e => setWebhookUrl(e.target.value)}
                />
              </div>
              {formError && <p className="text-[12px] text-red-600">{formError}</p>}
              <div className="flex gap-2">
                <Button size="sm" onClick={handleCreate} disabled={saving}>
                  {saving && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}
                  Create Rule
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setShowForm(false)}>Cancel</Button>
              </div>
            </div>
          )}

          {rules.length === 0 && !showForm ? (
            <p className="py-6 text-center text-[13px] text-slate-400">
              No rules yet. Create one to start watching your metrics.
            </p>
          ) : (
            <div className="divide-y divide-slate-100">
              {rules.map(rule => (
                <div key={rule.id} className="flex items-center justify-between gap-3 py-3 first:pt-0 last:pb-0">
                  <div className="min-w-0">
                    <p className={cn("text-[13px] font-medium truncate", rule.enabled ? "text-brand-950" : "text-slate-400")}>
                      {describeRule(rule)}
                    </p>
                    <p className="text-[11px] text-slate-400 mt-0.5">
                      {rule.webhook_url ? "In-app + webhook" : "In-app"} · cooldown {rule.cooldown_hours}h
                      {rule.last_triggered_at && ` · last fired ${new Date(rule.last_triggered_at.replace(" ", "T") + "Z").toLocaleDateString("en-US", { month: "short", day: "numeric" })}`}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button variant="ghost" size="icon" title={rule.enabled ? "Pause" : "Resume"} onClick={() => handleToggle(rule)}>
                      {rule.enabled ? <Bell className="w-4 h-4 text-brand-600" /> : <BellOff className="w-4 h-4 text-slate-400" />}
                    </Button>
                    <Button variant="ghost" size="icon" className="text-slate-400 hover:text-red-600" onClick={() => handleDeleteRule(rule.id)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Event feed */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                Triggered Alerts
                {unacked > 0 && <Badge variant="error">{unacked} new</Badge>}
              </CardTitle>
              <CardDescription>Most recent first</CardDescription>
            </div>
            {unacked > 0 && (
              <Button variant="secondary" size="sm" onClick={handleAckAll}>
                <CheckCheck className="w-3.5 h-3.5 mr-1.5" />
                Mark all read
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {events.length === 0 ? (
            <EmptyState
              icon={Bell}
              title="No alerts yet"
              description="When a rule's condition is met during a run, the alert lands here (and on the rule's webhook, if set)."
              className="border-0 py-10"
            />
          ) : (
            <div className="divide-y divide-slate-100">
              {events.map(event => (
                <div key={event.id} className={cn("flex items-center justify-between gap-3 py-3 first:pt-0 last:pb-0", event.acknowledged && "opacity-55")}>
                  <div className="min-w-0">
                    <p className="text-[13px] text-brand-950 leading-snug">{event.message}</p>
                    <p className="text-[11px] text-slate-400 mt-0.5">
                      {new Date(event.fired_at.replace(" ", "T") + "Z").toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                    </p>
                  </div>
                  {!event.acknowledged && (
                    <Button variant="ghost" size="icon" title="Mark read" onClick={() => handleAck(event.id)}>
                      <Check className="w-4 h-4 text-slate-400 hover:text-green-600" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
